import React, { useState, useEffect, useCallback } from 'react';
import { X, Plus, Pencil, Trash2 } from 'lucide-react';
import axios from 'axios';
import { signIn, signOut, fetchAuthSession } from 'aws-amplify/auth';
import { getRestApiBaseUrl, isAmplifyAuthConfigured } from '../amplifyConfigure';

const API_BASE = getRestApiBaseUrl();

async function authHeaders() {
  const session = await fetchAuthSession();
  const token = session.tokens?.idToken?.toString();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

const JOURS_OPTIONS = [
  { value: '', label: 'Choisir...' },
  { value: '1 jour / semaine', label: '1 jour / semaine' },
  { value: '2 jours / semaine', label: '2 jours / semaine' },
  { value: '3 jours / semaine', label: '3 jours / semaine' },
  { value: '4 jours / semaine', label: '4 jours / semaine' },
  { value: '5 jours / semaine', label: '5 jours / semaine' },
];

const EMPTY_FORM = { prenom: '', nom: '', telephone: '', departement: '', jours_semaine: '' };

const inputCls =
  'w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-[#1A3B48] focus:outline-none focus:border-[#05BF87] transition-colors bg-white';
const labelCls = 'block text-sm font-medium text-[#1A3B48] mb-1';

// ── Modal ────────────────────────────────────────────────────────────────────
function InscriptionModal({ mode, formData, onChange, onSave, onClose, saving }) {
  const isValid = Object.values(formData).every((v) => v.trim());
  const [showError, setShowError] = useState(false);

  const handleSave = () => {
    if (!isValid) { setShowError(true); return; }
    onSave();
  };

  useEffect(() => {
    if (showError && isValid) setShowError(false);
  }, [isValid, showError]);

  // Close on Escape
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(0,0,0,0.45)' }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      data-testid="inscription-modal"
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg">
        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-6 pb-4 border-b border-gray-100">
          <h2 className="text-lg font-bold text-[#1A3B48]">
            {mode === 'add' ? 'Ajouter un membre' : 'Modifier l\'inscription'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors p-1 rounded-lg hover:bg-gray-100"
            aria-label="Fermer"
            data-testid="modal-close"
          >
            <X size={18} />
          </button>
        </div>

        {/* Form */}
        <div className="px-6 py-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label htmlFor="modal-prenom" className={labelCls}>Prénom</label>
            <input
              id="modal-prenom"
              type="text"
              placeholder="Marie"
              value={formData.prenom}
              onChange={(e) => onChange('prenom', e.target.value)}
              className={inputCls}
              data-testid="modal-input-prenom"
            />
          </div>
          <div>
            <label htmlFor="modal-nom" className={labelCls}>Nom</label>
            <input
              id="modal-nom"
              type="text"
              placeholder="Dupont"
              value={formData.nom}
              onChange={(e) => onChange('nom', e.target.value)}
              className={inputCls}
              data-testid="modal-input-nom"
            />
          </div>
          <div>
            <label htmlFor="modal-telephone" className={labelCls}>Téléphone</label>
            <input
              id="modal-telephone"
              type="tel"
              placeholder="06 00 00 00 00"
              value={formData.telephone}
              onChange={(e) => onChange('telephone', e.target.value)}
              className={inputCls}
              data-testid="modal-input-telephone"
            />
          </div>
          <div>
            <label htmlFor="modal-departement" className={labelCls}>Département(s)</label>
            <input
              id="modal-departement"
              type="text"
              placeholder="Ex: 75011, Rhône..."
              value={formData.departement}
              onChange={(e) => onChange('departement', e.target.value)}
              className={inputCls}
              data-testid="modal-input-departement"
            />
          </div>
          <div className="sm:col-span-2">
            <label htmlFor="modal-jours" className={labelCls}>Jours disponibles / semaine</label>
            <select
              id="modal-jours"
              value={formData.jours_semaine}
              onChange={(e) => onChange('jours_semaine', e.target.value)}
              className={`${inputCls} cursor-pointer`}
              data-testid="modal-select-jours"
            >
              {JOURS_OPTIONS.map((o) => (
                <option key={o.value} value={o.value} disabled={o.value === ''}>{o.label}</option>
              ))}
            </select>
          </div>
        </div>

        {showError && (
          <p className="mx-6 mb-2 text-sm text-red-500" role="alert" data-testid="modal-error">
            Veuillez remplir tous les champs.
          </p>
        )}

        {/* Footer */}
        <div className="px-6 pb-6 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
            data-testid="modal-cancel"
          >
            Annuler
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-5 py-2 text-sm font-medium text-white bg-[#1A3B48] rounded-lg hover:bg-[#132d37] transition-colors disabled:opacity-60"
            data-testid="modal-save"
          >
            {saving ? 'Enregistrement...' : (mode === 'add' ? 'Ajouter' : 'Enregistrer')}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Delete Confirm ────────────────────────────────────────────────────────────
function DeleteConfirm({ onConfirm, onCancel, deleting }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onCancel(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(0,0,0,0.45)' }}
      onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}
      data-testid="delete-confirm-modal"
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6">
        <div className="flex items-start gap-4 mb-5">
          <div className="w-10 h-10 rounded-full bg-red-50 flex items-center justify-center flex-shrink-0 mt-0.5">
            <Trash2 size={18} className="text-red-500" />
          </div>
          <div>
            <h3 className="font-bold text-[#1A3B48] mb-1">Supprimer l'inscription ?</h3>
            <p className="text-sm text-gray-500">Cette action est irréversible. L'inscription sera définitivement supprimée.</p>
          </div>
        </div>
        <div className="flex justify-end gap-3">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-sm font-medium text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
            data-testid="delete-cancel"
          >
            Annuler
          </button>
          <button
            onClick={onConfirm}
            disabled={deleting}
            className="px-4 py-2 text-sm font-medium text-white bg-red-500 rounded-lg hover:bg-red-600 transition-colors disabled:opacity-60"
            data-testid="delete-confirm"
          >
            {deleting ? 'Suppression...' : 'Supprimer'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────
export default function AdminPage() {
  const [loggedIn, setLoggedIn] = useState(false);
  const [sessionChecked, setSessionChecked] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [inscriptions, setInscriptions] = useState([]);
  const [loadingData, setLoadingData] = useState(false);
  const [exporting, setExporting] = useState(false);

  // Modal state
  const [modal, setModal] = useState({ open: false, mode: 'add', inscription: null });
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  // Delete state
  const [deleteState, setDeleteState] = useState({ open: false, id: null });
  const [deleting, setDeleting] = useState(false);

  const fetchInscriptions = useCallback(async () => {
    if (!API_BASE) return;
    setLoadingData(true);
    try {
      const headers = await authHeaders();
      const { data } = await axios.get(`${API_BASE}/api/admin/inscriptions`, { headers });
      setInscriptions(data);
    } catch (err) {
      if (err.response?.status === 401) {
        try { await signOut(); } catch { /* ignore */ }
        setLoggedIn(false);
      }
    } finally {
      setLoadingData(false);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    if (!isAmplifyAuthConfigured()) {
      setSessionChecked(true);
      return;
    }
    const timeout = setTimeout(() => {
      if (!cancelled) setSessionChecked(true);
    }, 2000);
    (async () => {
      try {
        const session = await fetchAuthSession();
        if (!cancelled && session.tokens?.idToken) setLoggedIn(true);
      } catch {
        /* pas de session valide */
      }
      if (!cancelled) {
        clearTimeout(timeout);
        setSessionChecked(true);
      }
    })();
    return () => {
      cancelled = true;
      clearTimeout(timeout);
    };
  }, []);

  useEffect(() => {
    if (loggedIn && API_BASE) fetchInscriptions();
  }, [loggedIn, fetchInscriptions]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    try {
      const result = await signIn({ username: email.trim().toLowerCase(), password });
      if (result.isSignedIn) {
        setLoggedIn(true);
      } else {
        setLoginError('Etape requise: ' + (result.nextStep?.signInStep || 'inconnue'));
      }
    } catch (err) {
      if (err?.name === 'UserAlreadyAuthenticatedException') {
        setLoggedIn(true);
        return;
      }
      const msg =
        err?.message ||
        err?.name ||
        (typeof err === 'string' ? err : null) ||
        'Email ou mot de passe incorrect';
      setLoginError(msg);
    }
  };

  const handleExport = async () => {
    if (!API_BASE) return;
    setExporting(true);
    try {
      const headers = await authHeaders();
      const response = await axios.get(`${API_BASE}/api/admin/inscriptions/export`, {
        headers,
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'inscriptions_assisttoday.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export error:', err);
    } finally {
      setExporting(false);
    }
  };

  const handleLogout = async () => {
    try { await signOut(); } catch { /* ignore */ }
    setLoggedIn(false);
    setInscriptions([]);
  };

  // Modal helpers
  const openAdd = () => {
    setFormData(EMPTY_FORM);
    setModal({ open: true, mode: 'add', inscription: null });
  };

  const openEdit = (insc) => {
    setFormData({
      prenom: insc.prenom || '',
      nom: insc.nom || '',
      telephone: insc.telephone || '',
      departement: insc.departement || '',
      jours_semaine: insc.jours_semaine || '',
    });
    setModal({ open: true, mode: 'edit', inscription: insc });
  };

  const closeModal = () => setModal({ open: false, mode: 'add', inscription: null });

  const handleFormChange = (field, value) => setFormData((prev) => ({ ...prev, [field]: value }));

  const handleSave = async () => {
    if (!API_BASE) return;
    setSaving(true);
    try {
      if (modal.mode === 'add') {
        await axios.post(`${API_BASE}/api/inscriptions`, formData);
      } else {
        const headers = await authHeaders();
        await axios.put(`${API_BASE}/api/admin/inscriptions/${modal.inscription.id}`, formData, { headers });
      }
      closeModal();
      fetchInscriptions();
    } catch (err) {
      console.error('Save error:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!API_BASE) return;
    setDeleting(true);
    try {
      const headers = await authHeaders();
      await axios.delete(`${API_BASE}/api/admin/inscriptions/${deleteState.id}`, { headers });
      setDeleteState({ open: false, id: null });
      fetchInscriptions();
    } catch (err) {
      console.error('Delete error:', err);
    } finally {
      setDeleting(false);
    }
  };

  // ── Login screen ────────────────────────────────────────────────────────────
  if (!sessionChecked) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <p className="text-gray-500 text-sm">Vérification de la session…</p>
      </div>
    );
  }

  if (!isAmplifyAuthConfigured()) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-lg p-8 w-full max-w-lg text-sm text-gray-600 leading-relaxed">
          <h1 className="text-xl font-bold text-[#1A3B48] mb-3">Configuration Amplify requise</h1>
          <p className="mb-3">
            Déployez le backend avec <code className="bg-gray-100 px-1 rounded">npx ampx sandbox</code> (ou un
            déploiement pipeline), puis copiez le fichier{' '}
            <code className="bg-gray-100 px-1 rounded">amplify_outputs.json</code> généré à la racine du projet
            dans <code className="bg-gray-100 px-1 rounded">frontend/src/amplify_outputs.json</code> (en remplaçant
            les valeurs placeholder).
          </p>
          <p>
            Créez un utilisateur administrateur dans la console Cognito (User pool) avec une adresse e-mail et un
            mot de passe, puis connectez-vous avec ces identifiants.
          </p>
        </div>
      </div>
    );
  }

  if (!loggedIn) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-lg p-8 w-full max-w-sm">
          <div className="mb-6">
            <h1 className="text-2xl font-bold text-[#1A3B48]">Administration</h1>
            <p className="text-gray-500 text-sm mt-1">Accès réservé à l'équipe AssisToday (Cognito)</p>
          </div>
          <form onSubmit={handleLogin}>
            <div className="mb-4">
              <label htmlFor="admin-email" className="block text-sm font-medium text-[#1A3B48] mb-1.5">Email</label>
              <input
                id="admin-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-[#1A3B48] focus:outline-none focus:border-[#05BF87] transition-colors"
                placeholder="admin@assisttoday.fr"
                data-testid="admin-email-input"
                required
              />
            </div>
            <div className="mb-5">
              <label htmlFor="admin-password" className="block text-sm font-medium text-[#1A3B48] mb-1.5">Mot de passe</label>
              <input
                id="admin-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-[#1A3B48] focus:outline-none focus:border-[#05BF87] transition-colors"
                placeholder="••••••••"
                data-testid="admin-password-input"
                required
              />
            </div>
            {loginError && (
              <p className="text-red-500 text-sm mb-4" data-testid="admin-login-error">{loginError}</p>
            )}
            <button
              type="submit"
              className="w-full bg-[#1A3B48] text-white font-medium py-2.5 rounded-lg hover:bg-[#132d37] transition-colors text-sm"
              data-testid="admin-login-submit"
              aria-label="Se connecter à l'administration"
            >
              Se connecter
            </button>
          </form>
        </div>
      </div>
    );
  }

  // ── Dashboard ───────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-gray-50" style={{ fontFamily: "'Circular Std', sans-serif" }}>

      {/* Modals */}
      {modal.open && (
        <InscriptionModal
          mode={modal.mode}
          formData={formData}
          onChange={handleFormChange}
          onSave={handleSave}
          onClose={closeModal}
          saving={saving}
        />
      )}
      {deleteState.open && (
        <DeleteConfirm
          onConfirm={handleDelete}
          onCancel={() => setDeleteState({ open: false, id: null })}
          deleting={deleting}
        />
      )}

      {!API_BASE && (
        <div
          className="mx-6 mt-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900"
          role="status"
        >
          URL d’API absente : définissez <code className="bg-white/80 px-1 rounded">REACT_APP_BACKEND_URL</code> au
          build, ou renseignez <code className="bg-white/80 px-1 rounded">custom.restApiUrl</code> dans{' '}
          <code className="bg-white/80 px-1 rounded">frontend/src/amplify_outputs.json</code> après{' '}
          <code className="bg-white/80 px-1 rounded">ampx sandbox</code>.
        </div>
      )}

      {/* Header */}
      <header className="bg-white border-b border-gray-200 px-6 py-4 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <div>
          <h1 className="text-xl font-bold text-[#1A3B48]">Tableau de bord AssisToday</h1>
          <p className="text-gray-500 text-sm mt-0.5">
            {loadingData ? 'Chargement...' : `${inscriptions.length} inscription(s) enregistrée(s)`}
          </p>
        </div>
        <div className="flex flex-wrap gap-3">
          <button
            onClick={openAdd}
            className="flex items-center gap-1.5 bg-[#1A3B48] text-white font-medium px-4 py-2 rounded-lg hover:bg-[#132d37] transition-all text-sm"
            data-testid="add-member-button"
            aria-label="Ajouter un membre manuellement"
          >
            <Plus size={15} />
            Ajouter un membre
          </button>
          <button
            onClick={handleExport}
            disabled={exporting || inscriptions.length === 0}
            className="bg-[#05BF87] text-white font-medium px-4 py-2 rounded-lg hover:opacity-90 transition-all text-sm disabled:opacity-50"
            data-testid="export-excel-button"
            aria-label="Exporter les inscriptions en fichier Excel"
          >
            {exporting ? 'Export...' : 'Exporter en Excel (.xlsx)'}
          </button>
          <button
            onClick={handleLogout}
            className="border border-gray-200 text-gray-600 font-medium px-4 py-2 rounded-lg hover:bg-gray-50 transition-all text-sm"
            data-testid="admin-logout-button"
            aria-label="Se déconnecter"
          >
            Déconnexion
          </button>
        </div>
      </header>

      {/* Main */}
      <main className="p-6">
        {loadingData ? (
          <div className="text-center py-16 text-gray-400 text-sm">Chargement des inscriptions...</div>
        ) : inscriptions.length === 0 ? (
          <div className="text-center py-16" data-testid="no-inscriptions">
            <p className="text-gray-400 text-sm mb-4">Aucune inscription pour le moment.</p>
            <button
              onClick={openAdd}
              className="inline-flex items-center gap-1.5 bg-[#1A3B48] text-white font-medium px-4 py-2 rounded-lg hover:bg-[#132d37] transition-all text-sm"
              data-testid="add-member-empty"
            >
              <Plus size={15} />
              Ajouter le premier membre
            </button>
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-200">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="inscriptions-table">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    {["Prénom", "Nom", "Téléphone", "Département(s)", "Jours disponibles / semaine", "Date d'inscription", "Actions"].map((h) => (
                      <th
                        key={h}
                        className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider px-4 py-3 whitespace-nowrap"
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {inscriptions.map((insc, i) => (
                    <tr key={insc.id || i} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3 font-medium text-[#1A3B48]">{insc.prenom}</td>
                      <td className="px-4 py-3 text-[#1A3B48]">{insc.nom}</td>
                      <td className="px-4 py-3 text-[#1A3B48]">{insc.telephone}</td>
                      <td className="px-4 py-3 text-[#1A3B48]">{insc.departement}</td>
                      <td className="px-4 py-3 text-[#1A3B48]">{insc.jours_semaine}</td>
                      <td className="px-4 py-3 text-gray-500 whitespace-nowrap">
                        {insc.created_at
                          ? new Date(insc.created_at).toLocaleString('fr-FR', {
                              day: '2-digit', month: '2-digit', year: 'numeric',
                              hour: '2-digit', minute: '2-digit',
                            })
                          : '—'}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => openEdit(insc)}
                            className="inline-flex items-center gap-1 text-xs font-medium text-[#1A3B48] border border-gray-200 px-2.5 py-1.5 rounded-lg hover:bg-gray-50 transition-colors"
                            aria-label={`Modifier l'inscription de ${insc.prenom} ${insc.nom}`}
                            data-testid={`edit-button-${insc.id || i}`}
                          >
                            <Pencil size={12} />
                            Modifier
                          </button>
                          <button
                            onClick={() => setDeleteState({ open: true, id: insc.id })}
                            className="inline-flex items-center gap-1 text-xs font-medium text-red-500 border border-red-100 px-2.5 py-1.5 rounded-lg hover:bg-red-50 transition-colors"
                            aria-label={`Supprimer l'inscription de ${insc.prenom} ${insc.nom}`}
                            data-testid={`delete-button-${insc.id || i}`}
                          >
                            <Trash2 size={12} />
                            Supprimer
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
